import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { LayoutGrid, List as ListIcon, Search, ChevronRight, Target } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { VerificationStrip } from '@/features/OrgLayout/VerificationStrip';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { useOrgStudents } from '@/lib/hooks/useOrg';
import { useReadinessFormula } from '@/lib/hooks/useReadiness';
import { ReadinessFormulaSummary, formatComputedAt } from '@/features/Readiness';
import { BAND_META, BAND_ORDER } from '@/features/OrgLayout/readiness';
import type { OrgStudentListItem, ReadinessBand } from '@/lib/api/org';

export const Route = createFileRoute('/org/readiness')({
  component: ReadinessPage,
});

const BAND_SUBTITLE: Record<ReadinessBand, string> = {
  product_ready: 'Ready for product-based roles',
  service_ready: 'Ready for service-based roles',
  developing: 'Students in active development',
  needs_support: 'Students needing extra support',
};

// Score text color per band (matches the band dot family).
const BAND_SCORE: Record<ReadinessBand, string> = {
  product_ready: 'text-emerald-600',
  service_ready: 'text-blue-600',
  developing: 'text-amber-600',
  needs_support: 'text-red-600',
};

const SELECT_TRIGGER = 'h-[42px] w-auto min-w-[150px] rounded-[14px] border-[#ececec] bg-white px-[17px] text-sm font-medium text-[#6b7280]';

/**
 * Scores are recomputed in batches, so timestamps normally cluster within a
 * day and one board-level line is enough. When they genuinely diverge by more
 * than a day the summary also names the oldest, so the spread is visible
 * without repeating a stamp on every card.
 */
const SPREAD_MS = 24 * 60 * 60 * 1000;

/**
 * A score built on a third of the formula must not look identical to one built
 * on all of it. Below this, the roster prints the coverage as a caveat.
 */
const LOW_COVERAGE = 0.75;

/** "based on 45% of the formula", or null when coverage is unremarkable. */
function coverageNote(student: OrgStudentListItem): string | null {
  const coverage = student.data_coverage;
  if (typeof coverage !== 'number' || coverage <= 0 || coverage >= LOW_COVERAGE) return null;
  return `based on ${Math.round(coverage * 100)}% of the formula`;
}

/** A score with no computed_at was never calculated — it is not "now", and not 0. */
const isScored = (student: OrgStudentListItem) => !!student.computed_at;

function RosterFreshness({ students }: { students: OrgStudentListItem[] }) {
  const times = students
    .map((s) => (s.computed_at ? new Date(s.computed_at).getTime() : NaN))
    .filter((t) => !Number.isNaN(t));
  const unscored = students.length - times.length;

  if (times.length === 0) {
    return (
      <p className="text-xs text-[#9ca3af]">
        No readiness scores have been calculated yet.
      </p>
    );
  }

  const latest = Math.max(...times);
  const earliest = Math.min(...times);
  const spread = latest - earliest;

  return (
    <p className="text-xs text-[#9ca3af]">
      {`Scores as of ${formatComputedAt(new Date(latest).toISOString())}`}
      {spread > SPREAD_MS && ` · oldest ${formatComputedAt(new Date(earliest).toISOString())}`}
      {unscored > 0 &&
        ` · ${unscored} student${unscored === 1 ? '' : 's'} not scored yet`}
    </p>
  );
}

function ReadinessPage() {
  const { data, isLoading, isError, refetch } = useOrgStudents();
  // The old static line here claimed the score used "mock performance" (an
  // input that has never existed) and omitted curriculum progress. The
  // explanation is now generated from the formula the scorer itself uses.
  const { data: formula, isLoading: formulaLoading } = useReadinessFormula();
  const navigate = useNavigate();
  const [view, setView] = useState<'kanban' | 'list'>('kanban');
  const [search, setSearch] = useState('');
  const [batch, setBatch] = useState('all');

  const batches = useMemo(() => {
    const m = new Map<string, string>();
    (data ?? []).forEach((s) => m.set(s.batch_id, s.batch_name));
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter((s) => {
      if (batch !== 'all' && s.batch_id !== batch) return false;
      if (q && !s.name.toLowerCase().includes(q) && !s.email.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, search, batch]);

  const grouped = useMemo(() => {
    const g: Record<ReadinessBand, typeof filtered> = { product_ready: [], service_ready: [], developing: [], needs_support: [] };
    filtered.forEach((s) => g[s.readiness_band]?.push(s));
    (Object.keys(g) as ReadinessBand[]).forEach((b) => g[b].sort((a, z) => z.readiness_score - a.readiness_score));
    return g;
  }, [filtered]);

  const goToStudent = (batchId: string, userId: string) =>
    navigate({ to: '/org/batches/$batchId/students/$userId', params: { batchId, userId } });

  if (isLoading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-9 w-64" />
        <div className="grid gap-4 lg:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-[24px]" />)}</div>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <p className="text-[#6b7280]">Couldn&apos;t load placement readiness.</p>
        <Button className="mt-4" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }
  if ((data?.length ?? 0) === 0) {
    return (
      <div className="space-y-6">
        <Header />
        <EmptyState icon={Target} title="No students yet" description="Once students are enrolled and active, their placement readiness appears here." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header />

      {/*
        Directly under the bands it supports. Integrity's job on this screen is
        to make the readiness claim credible, not to be a subject of its own.
      */}
      <VerificationStrip />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-[42px] w-[280px] items-center gap-3 rounded-[14px] border border-[#ececec] bg-white px-[17px]">
            <Search className="h-[15px] w-[15px] flex-shrink-0 text-[#6b7280]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email..." className="w-full border-none bg-transparent text-sm text-[#111827] placeholder:text-[rgba(107,114,128,0.5)] focus:outline-none" />
          </div>
          <Select value={batch} onValueChange={setBatch}>
            <SelectTrigger className={SELECT_TRIGGER}><SelectValue placeholder="All Batches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Batches</SelectItem>
              {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* Kanban / List toggle */}
        <div className="flex items-center gap-1 rounded-[14px] border border-[#ececec] bg-white p-1">
          <ToggleBtn active={view === 'kanban'} onClick={() => setView('kanban')} icon={LayoutGrid} label="Kanban" />
          <ToggleBtn active={view === 'list'} onClick={() => setView('list')} icon={ListIcon} label="List" />
        </div>
      </div>

      {/* One freshness line for the whole board (scores are recomputed in
          batches, so per-card stamps would repeat the same value); it names the
          oldest score when the timestamps actually diverge. */}
      <RosterFreshness students={filtered} />

      {view === 'kanban' ? (
        <div className="grid gap-4 lg:grid-cols-4">
          {BAND_ORDER.map((band) => {
            const meta = BAND_META[band];
            const list = grouped[band];
            return (
              <OrgCard key={band} className="flex flex-col p-4">
                <div className="flex items-center gap-2">
                  <span className={cn('h-2.5 w-2.5 rounded-full', meta.dotClass)} />
                  <h3 className="text-sm font-semibold text-[#111827]">{meta.label}</h3>
                  <span className="ml-auto rounded-full bg-[#f3f4f6] px-2 py-0.5 text-xs font-semibold text-[#6b7280]">{list.length}</span>
                </div>
                <p className="mt-0.5 text-xs text-[#9ca3af]">{BAND_SUBTITLE[band]}</p>
                <div className="mt-3 flex-1 space-y-2">
                  {list.length === 0 ? (
                    <p className="py-6 text-center text-xs text-[#9ca3af]">No students</p>
                  ) : (
                    list.slice(0, 5).map((s) => (
                      <button key={`${s.batch_id}-${s.user_id}`} type="button" onClick={() => goToStudent(s.batch_id, s.user_id)} className="flex w-full items-center gap-2.5 rounded-xl border border-[#ececec] px-3 py-2 text-left hover:bg-[#fafafa]">
                        <OrgAvatar name={s.name} size={30} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-[#111827]">{s.name}</p>
                          <p className="truncate text-xs text-[#9ca3af]">
                            {s.batch_name}
                            {coverageNote(s) && ` · ${coverageNote(s)}`}
                          </p>
                        </div>
                        {isScored(s) ? (
                          <span className={cn('text-sm font-bold tabular-nums', BAND_SCORE[band])}>{Math.round(s.readiness_score)}</span>
                        ) : (
                          // Never computed: a number here would be fabricated.
                          <span className="text-xs text-[#9ca3af]" title="Not scored yet">—</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
                {list.length > 5 && (
                  <button type="button" onClick={() => setView('list')} className="mt-2 text-xs font-medium text-[#ff5a1f]">View all {list.length} students →</button>
                )}
              </OrgCard>
            );
          })}
        </div>
      ) : (
        <OrgCard className="overflow-hidden">
          {filtered.length === 0 ? (
            <p className="py-16 text-center text-sm text-[#9ca3af]">No students match your filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse">
                <thead>
                  <tr className="border-b border-[#ececec]">
                    {['Student', 'Batch', 'Readiness', 'Score', ''].map((h, i) => (
                      <th key={i} className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...filtered].sort((a, z) => z.readiness_score - a.readiness_score).map((s, i, arr) => (
                    <tr key={`${s.batch_id}-${s.user_id}`} onClick={() => goToStudent(s.batch_id, s.user_id)} className={cn('cursor-pointer hover:bg-[#fafafa]', i !== arr.length - 1 && 'border-b border-[#ececec]')}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <OrgAvatar name={s.name} />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-[#111827]">{s.name}</p>
                            <p className="truncate text-xs text-[#9ca3af]">{s.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#374151]">{s.batch_name}</td>
                      <td className="px-6 py-4">
                        <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium', BAND_META[s.readiness_band].badgeClass)}>
                          <span className={cn('h-1.5 w-1.5 rounded-full', BAND_META[s.readiness_band].dotClass)} />
                          {BAND_META[s.readiness_band].label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {isScored(s) ? (
                          <>
                            <span className={cn('text-sm font-bold tabular-nums', BAND_SCORE[s.readiness_band])}>
                              {Math.round(s.readiness_score)}
                            </span>
                            {coverageNote(s) && (
                              <span className="mt-0.5 block text-[11px] text-[#9ca3af]">{coverageNote(s)}</span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-[#9ca3af]">Not scored yet</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right"><ChevronRight className="inline h-4 w-4 text-[#9ca3af]" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </OrgCard>
      )}

      <ReadinessFormulaSummary formula={formula} isLoading={formulaLoading} />
    </div>
  );
}

function Header() {
  return (
    <div>
      <h1 className="text-[36px] font-bold leading-tight text-[#111827]">Placement Readiness</h1>
      <p className="pt-1 text-base text-[#6b7280]">Track and manage student placement readiness across all batches.</p>
    </div>
  );
}

function ToggleBtn({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: typeof LayoutGrid; label: string }) {
  return (
    <button type="button" onClick={onClick} className={cn('inline-flex items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-sm font-medium transition-colors', active ? 'bg-[#fff1ec] text-[#ff5a1f]' : 'text-[#6b7280] hover:text-[#111827]')}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}
